import { useState, useEffect, createContext, useContext } from "react";
import { ShoppingCart, X, Trash2, Send, Plus, Minus } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

interface QuoteItem {
  id: string;
  name: string;
  image: string;
  quantity: number;
  variant?: string;
}

interface QuoteContextType {
  items: QuoteItem[];
  addItem: (item: Omit<QuoteItem, "quantity">) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearAll: () => void;
  count: number;
}

const QuoteContext = createContext<QuoteContextType | null>(null);

export const useQuoteBuilder = () => {
  const ctx = useContext(QuoteContext);
  if (!ctx) throw new Error("useQuoteBuilder must be used within QuoteBuilderProvider");
  return ctx;
};

export const QuoteBuilderProvider = ({ children }: { children: React.ReactNode }) => {
  const [items, setItems] = useState<QuoteItem[]>(() => {
    try {
      const stored = localStorage.getItem("quote-builder-items");
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem("quote-builder-items", JSON.stringify(items));
  }, [items]);

  const addItem = (item: Omit<QuoteItem, "quantity">) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.id === item.id);
      if (existing) return prev.map((i) => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { ...item, quantity: 1 }];
    });
  };

  const removeItem = (id: string) => setItems((prev) => prev.filter((i) => i.id !== id));
  const updateQuantity = (id: string, quantity: number) => {
    if (quantity < 1) return removeItem(id);
    setItems((prev) => prev.map((i) => i.id === id ? { ...i, quantity } : i));
  };
  const clearAll = () => setItems([]);

  return (
    <QuoteContext.Provider value={{ items, addItem, removeItem, updateQuantity, clearAll, count: items.length }}>
      {children}
    </QuoteContext.Provider>
  );
};

const QuoteBuilder = () => {
  const { items, removeItem, updateQuantity, clearAll, count } = useQuoteBuilder();

  if (count === 0) return null;

  const quoteSummary = items.map((i) => `${i.name} x${i.quantity}${i.variant ? ` (${i.variant})` : ""}`).join(", ");

  return (
    <div className="fixed bottom-20 right-4 z-40">
      <Sheet>
        <SheetTrigger asChild>
          <button className="relative bg-primary text-primary-foreground rounded-full w-14 h-14 flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
            <ShoppingCart className="w-6 h-6" />
            <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
              {count}
            </span>
          </button>
        </SheetTrigger>
        <SheetContent className="w-full sm:max-w-md bg-card border-border">
          <SheetHeader>
            <SheetTitle className="text-foreground">Quote Builder ({count} items)</SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-3 flex-1 overflow-y-auto max-h-[60vh]">
            {items.map((item) => (
              <div key={item.id} className="flex items-center gap-3 p-3 bg-secondary/30 rounded-lg">
                <img src={item.image} alt={item.name} className="w-12 h-12 rounded object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{item.name}</p>
                  {item.variant && <p className="text-xs text-muted-foreground">{item.variant}</p>}
                  <div className="flex items-center gap-2 mt-1">
                    <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="p-0.5 hover:bg-secondary rounded"><Minus className="w-3 h-3" /></button>
                    <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="p-0.5 hover:bg-secondary rounded"><Plus className="w-3 h-3" /></button>
                  </div>
                </div>
                <button onClick={() => removeItem(item.id)} className="p-1 hover:bg-destructive/20 rounded text-muted-foreground hover:text-destructive">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="mt-6 space-y-3">
            <Link to={`/contact?subject=Bulk Quote Request&message=Requesting quote for: ${encodeURIComponent(quoteSummary)}`}>
              <Button className="w-full gap-2" size="lg">
                <Send className="w-4 h-4" /> Request Bulk Quote
              </Button>
            </Link>
            <Button variant="outline" className="w-full" onClick={clearAll}>Clear All</Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default QuoteBuilder;
